import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {UserJwt} from '../../models/admin_owner/user/user-jwt';
import {RecoveryCodeSet, SecondFactorEnrolment, SecondFactorStatus} from './two-factor.models';

/**
 * Talks to the **monolith's** account second-factor endpoints (`/auth/account/two-factor/*`) — #282.
 *
 * Shaped on {@link FormsGateHttpService}, and for the same reasons: these live on the monolith origin (the
 * SPA's single `BASE_URL`), all of them stay reachable while the session is `restricted` (the gate whitelists
 * them, or the remediation screen could never remediate), the `AuthInterceptor` attaches the bearer, and
 * `withCredentials` is what carries the `CM-Secret` cookie the global `EncryptionTokenMiddleware` needs to
 * decrypt that bearer — without it every call here 401s with "Secret not found." before the JWT is looked at.
 *
 * **Why this is its own service and not more methods on `AuthenticationService`.** That service owns the
 * *session*: minting it, storing it, reading its claims. These seven endpoints administer a credential and hand
 * back nothing the session cares about — with one exception, {@link reissue}, which is here because it is the
 * same URL family and the caller feeds its result to `AuthenticationService.applyReissuedSession(...)`, exactly
 * as the forms gate does. The **second step of a sign-in** deliberately went the other way: it produces a
 * session and must store it identically to `login()`, so it lives on `AuthenticationService`.
 *
 * Nothing here is cached. A status read is cheap, and a stale "recovery codes remaining" is precisely the kind
 * of number somebody makes a decision on.
 */
@Injectable({providedIn: 'root'})
export class SecondFactorHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /** The current account's second-factor status: enrolment, usage, recovery-code counts and company policy. */
  status(): Observable<SecondFactorStatus> {
    return this.http.get<SecondFactorStatus>(`${this.baseUrl}/auth/account/two-factor`, {withCredentials: true});
  }

  /**
   * Starts an enrolment: mints a pending secret and returns the QR/base32/URI **once**.
   *
   * Calling it again replaces the pending secret, which is the intended way to recover from a closed tab — so
   * the UI must discard any secret it is still showing when it calls this, or it will display one the server
   * has already forgotten.
   */
  enrol(): Observable<SecondFactorEnrolment> {
    return this.http.post<SecondFactorEnrolment>(`${this.baseUrl}/auth/account/two-factor/enrol`, {},
      {withCredentials: true});
  }

  /** Confirms the pending enrolment with a code from the app; the response is the initial recovery-code set. */
  confirm(code: string): Observable<RecoveryCodeSet> {
    return this.http.post<RecoveryCodeSet>(`${this.baseUrl}/auth/account/two-factor/confirm`, {Code: code},
      {withCredentials: true});
  }

  /**
   * Replaces the whole recovery-code set, proving possession with a current code first.
   *
   * The old set stops working the moment this succeeds — there is no merge — so the caller must show the result
   * before anything can navigate away from it.
   */
  regenerateRecoveryCodes(code: string): Observable<RecoveryCodeSet> {
    return this.http.post<RecoveryCodeSet>(`${this.baseUrl}/auth/account/two-factor/recovery-codes`, {Code: code},
      {withCredentials: true});
  }

  /** Removes the factor, proving possession with a current code. Refused (409) while the company requires it. */
  remove(code: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/two-factor/remove`, {Code: code},
      {withCredentials: true});
  }

  /**
   * Re-evaluates the account-security gate and mints a fresh session token — the live upgrade out of a
   * `restricted` session, with no re-login. Feed the result to `AuthenticationService.applyReissuedSession(...)`.
   */
  reissue(): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/account/two-factor/reissue`, {},
      {withCredentials: true});
  }
}
